<template>
  <div class="reference-currency-view">
    <div class="reference-currency-header">
      <div class="back-icon" @click="$emit('go-back')">
        <v-img
          max-height="24px"
          max-width="24px"
          :src="require('@components/assets/side-bar/backIcon.svg')"
        ></v-img>
      </div>
      <span class="currency-view-title">Currencies used in this account</span>
    </div>
    <span class="currecy-view-desc">
      This configuration enable features in compensation app
    </span>
    <hr class="hr mb-3 mt-6" />
    <div class="default-currency">
      <div class="default-currency-view">
        <span class="currency-text">Reference Currency</span>
        <span class="currency-text-desc">
          The currency you use to consolidate financials at HQ
        </span>
      </div>
      <CurrencyPicker
        pickerLabel="Currency"
        label="Select Currency"
        searchLabel="Search Currency"
        :value="defaultCurrency"
        :useCode="true"
        :isSelectedItemRemove="false"
        :isShowDefautValue="true"
        @item-currency-selected="handleItemSelected"
      />
    </div>
    <div>
      <CurrencyTable
        :dataSource="currencyList"
        :dropdownCurrencyList="dropdownCurrencyList"
        @onAddCurrency="$emit('onAddCurrency', $event)"
        @reloadData="$emit('reloadData')"
        @get-chatter-list="getCurrencyChatterList($event, 1)"
        :chatterList="chatterList"
        :totalCheckpointChatter="totalCurrencyChatter"
        @requestMoreChatter="loadMoreChatter"
        :storePersistFilters="true"
        storeFiltersKeyPrefix="currency-filter-"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from "vue";
import CurrencyPicker from "../../CurrencyPicker/CurrencyPicker.vue";
import { updateReferenceCurrency, getChatterList } from "@/api";
import CurrencyTable from "../../CurrencyTable/CurrencyTable.vue";

const instance = getCurrentInstance();
const $loader = instance?.proxy?.$loader;

const props = defineProps({
  defaultCurrency: {
    type: Object,
    default: null,
  },
  currencyList: {
    required: false,
    type: Array,
    default() {
      return [];
    },
  },
  dropdownCurrencyList: {
    required: false,
    type: Array,
    default() {
      return [];
    },
  },
});

const emit = defineEmits(["onAddCurrency", "reloadData", "go-back"]);

// Chatter state
const chatterList = ref([]);
const totalCurrencyChatter = ref(0);
const currentCurrencyData = ref(null);
const isLoadingChatter = ref(false);

const chatterPage = computed(() => {
  return Math.ceil(chatterList.value.length / 10) || 0;
});

const getCurrencyChatterList = async (item, page) => {
  try {
    isLoadingChatter.value = true;
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));

    // Store current currency data for load more functionality
    if (page === 1) {
      currentCurrencyData.value = item;
      chatterList.value = [];
    }

    const { data } = await getChatterList(accountId, item.id, "operatingcurrency", page);

    if (data && data.count !== undefined) {
      totalCurrencyChatter.value = data.count;
    }

    if (data && data.results && Array.isArray(data.results)) {
      let chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        let nameParts = el.recorded_by_name.split(" ");
        const newEl = {};
        newEl.id = el.id;

        if (nameParts.length === 2) {
          newEl.avatar = {
            picture: "",
            firstName: nameParts[0],
            lastName: nameParts[1],
          };
        } else {
          newEl.avatar = {
            picture: "",
            firstName: el.recorded_by_name,
            lastName: "",
          };
        }

        newEl.IsExIQtiveBot = false;
        newEl.message = truncateHtml(el.note);
        newEl.created_on = millisecondsString;
        newEl.IsHistory = el.is_history;
        return newEl;
      });

      chatterList.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error fetching chatter list:", error);
  } finally {
    isLoadingChatter.value = false;
  }
};

const loadMoreChatter = async () => {
  // Prevent multiple simultaneous requests
  if (isLoadingChatter.value) {
    return;
  }

  // Check if we have more data to load
  if (chatterList.value.length >= totalCurrencyChatter.value) {
    return;
  }

  if (currentCurrencyData.value) {
    const nextPage = chatterPage.value + 1;
    await getCurrencyChatterList(currentCurrencyData.value, nextPage);
  }
};

const truncateHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  let text = div.textContent || div.innerText || "";
  return text;
};

const handleItemSelected = async (selectedItem) => {
  $loader?.show();
  const account_id = JSON.parse(localStorage.getItem("currentAccountID"));
  const user = JSON.parse(localStorage.getItem("user"));
  let data = {
    name: user.name,
    currency: selectedItem.id,
  };
  // Use correct function signature: updateReferenceCurrency(user_id, account_id, data)
  await updateReferenceCurrency(user.id, account_id, data);
  localStorage.setItem("referenceCurrency", selectedItem.id);
  $loader?.hide();
};
</script>

<style scoped>
.reference-currency-view {
  margin: 0px 8px 0px 8px;
}
.reference-currency-header {
  margin: 0px 0px 0px 0px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.currency-view-title {
  color: #101828;
  font-family: Inter;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
}
.currecy-view-desc {
  overflow: hidden;
  color: #475467;
  text-overflow: ellipsis;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
.reference-currency-view .hr {
  border: 1px solid #f2f4f7;
}
.default-currency {
  display: flex;
  gap: 64px;
}
.default-currency-view {
  display: flex;
  min-width: 250px;
  max-width: 280px;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  margin-top: 14px;
  gap: 4px;
}
.currency-text {
  color: var(--colors-text-text-secondary-700, #344054);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
}
.currency-text-desc {
  color: var(--colors-text-text-tertiary-600, #475467);
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}
.back-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: url("../../assets/cursorArrow.svg"), auto;
  padding: 8px 12px 8px 10px;
}
.back-icon:hover {
  background-color: #f4f4f4;
  border-radius: 100%;
}
</style>
